<template>
  <div class="tw-px-4 tw-py-2">
    <label v-if="label" class="tw-block tw-text-xs tw-font-medium tw-text-nc-text-secondary tw-mb-1.5">{{ label }}</label>

    <!-- Single-select: native <select> -->
    <select
      v-if="!multiple"
      :value="modelValue"
      class="nc-select-el tw-w-full tw-bg-nc-bg-input tw-rounded-lg tw-px-3 tw-py-2.5 tw-pr-8 tw-text-sm tw-text-nc-text tw-border tw-border-nc-border tw-shadow-nc-sm tw-outline-none tw-appearance-none tw-transition-colors focus:tw-border-nc-primary focus:tw-ring-1 focus:tw-ring-nc-primary"
      @change="onSelectChange"
    >
      <option v-if="placeholder" value="" disabled selected>{{ placeholder }}</option>
      <option v-for="opt in options" :key="optKey(opt)" :value="opt.value">{{ opt.label }}</option>
    </select>

    <!-- Multi-select: chip-based UI -->
    <div v-else class="nc-multiselect">
      <div v-if="placeholder && selectedArray.length === 0" class="tw-text-xs tw-text-nc-medium tw-mb-2">{{ placeholder }}</div>
      <div class="tw-flex tw-flex-wrap tw-gap-2">
        <button
          v-for="opt in options"
          :key="optKey(opt)"
          type="button"
          class="nc-ms-chip"
          :class="{ 'nc-ms-chip--active': isSelected(opt) }"
          @click="toggleOption(opt)"
        >
          {{ opt.label }}
          <mdicon v-if="isSelected(opt)" name="check" size="14" />
        </button>
        <div v-if="options.length === 0" class="tw-text-xs tw-text-nc-medium">Geen opties beschikbaar</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NcSelect',
  props: {
    modelValue: { type: [String, Number, Array], default: '' },
    label: { type: String, default: '' },
    options: { type: Array, default: () => [] },
    placeholder: { type: String, default: '' },
    multiple: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  computed: {
    selectedArray() {
      return Array.isArray(this.modelValue) ? this.modelValue : [];
    },
  },
  methods: {
    onSelectChange(event) {
      // The native <select> only ever yields a string. Map it back to the
      // original option value so the emitted type (number/boolean/object) is
      // preserved — important for consumers like the measurement calculator.
      const raw = event.target.value;
      const opt = this.options.find((o) => String(o.value) === raw);
      this.$emit('update:modelValue', opt ? opt.value : raw);
    },
    optKey(opt) {
      if (opt == null) return '';
      if (opt.value == null) return opt.label;
      if (typeof opt.value === 'object') return opt.value.id ?? opt.value.uuid ?? JSON.stringify(opt.value);
      return opt.value;
    },
    sameValue(a, b) {
      if (a === b) return true;
      if (a == null || b == null) return false;
      if (typeof a === 'object' && typeof b === 'object') {
        if (a.id != null && b.id != null) return a.id === b.id;
        if (a.uuid != null && b.uuid != null) return a.uuid === b.uuid;
      }
      return false;
    },
    isSelected(opt) {
      return this.selectedArray.some(v => this.sameValue(v, opt.value));
    },
    toggleOption(opt) {
      const current = this.selectedArray.slice();
      const idx = current.findIndex(v => this.sameValue(v, opt.value));
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        current.push(opt.value);
      }
      this.$emit('update:modelValue', current);
    },
  },
};
</script>

<style scoped>
.nc-select-el {
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%2392949c' d='M7 10l5 5 5-5z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: 16px;
  background-position: right 12px center;
}

.nc-multiselect {
  width: 100%;
}

.nc-ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1.5px solid var(--nc-border);
  border-radius: var(--nc-radius-full);
  background: var(--nc-bg-card);
  font-size: 13px;
  font-weight: 500;
  color: var(--nc-text);
  cursor: pointer;
  transition: all 120ms ease;
  -webkit-tap-highlight-color: transparent;
}

.nc-ms-chip:active {
  transform: scale(0.97);
}

.nc-ms-chip--active {
  border-color: var(--nc-color-primary);
  background: var(--nc-color-primary-light);
  color: var(--nc-color-primary);
}
</style>
